import { Consts } from "./Consts.js";
import { VectorUtils } from "./VectorUtils.js";
/**
 * Contains useful functions to apply limits when drag points.
 */
// @dynamic
export class LimitUtils {
    /**
     * Projects newPos on a vector if it goes out of limit. //todo fix para tolerance
     */
    static applyLimitAndProjectVector(sidePoint1, sidePoint2, newPos, validPoint, tolerance, limitLine) {
        const distDir = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(sidePoint1, sidePoint2, validPoint, Consts.planeNormal).setLength(tolerance || this.tolerance);
        const newPosDist = newPos.clone().sub(distDir);
        if (!VectorUtils.arePointsOnSameSide(sidePoint1, sidePoint2, Consts.planeNormal, validPoint, newPosDist)) {
            newPos.copy(VectorUtils.projectOnVector(newPos, sidePoint1, sidePoint2).add(distDir).add(distDir.setLength(this.tolerance))); //to avoid sign = 0
        }
        limitLine?.updateLimit(sidePoint1, sidePoint2);
        return newPos;
    }
    /**
   * Sets a fixed point to newPos if it goes out of limit. testing to ovveride the first one
   */
    static applyLimitAndSetVector2(sidePoint1, sidePoint2, newPos, dir, validPoint, tolerance) {
        VectorUtils.invertDirIfOutOfValidArea(dir, sidePoint1, sidePoint2, validPoint);
        const distDir = dir.clone().setLength(tolerance || this.tolerance);
        const newPosDist = newPos.clone().sub(distDir);
        if (!VectorUtils.arePointsOnSameSide(sidePoint1, sidePoint2, Consts.planeNormal, validPoint, newPosDist)) {
            const intersection = VectorUtils.lines2DIntersection(newPos, newPos.clone().add(dir), sidePoint1, sidePoint2);
            intersection && newPos.copy(intersection.add(distDir).add(distDir.setLength(this.tolerance))); //to avoid sign = 0
            return true;
        }
        return false;
    }
    /**
     * Sets a fixed point to newPos if it goes out of limit.
     */
    static applyLimitAndSetVector(sidePoint1, sidePoint2, newPos, oldPos, validPoint, tolerance, limitLine) {
        const distDir = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(sidePoint1, sidePoint2, validPoint, Consts.planeNormal).setLength(tolerance || this.tolerance);
        sidePoint1 = sidePoint1.clone().add(distDir);
        sidePoint2 = sidePoint2.clone().add(distDir);
        if (!VectorUtils.arePointsOnSameSide(sidePoint1, sidePoint2, Consts.planeNormal, validPoint, newPos)) {
            const intersection = VectorUtils.lines2DIntersection(oldPos, newPos, sidePoint1, sidePoint2);
            intersection && newPos.copy(intersection); //sign could not be 0 because of distDir
        }
        limitLine?.updateLimit(sidePoint1, sidePoint2);
        return newPos;
    }
    /**
     * Projects newPos on a vector if A or B go out of limit.
     */
    static applyLimitAndProjectVectorCenterLinePoint(sidePoint1, sidePoint2, oldC, newC, A, B, validPoint, limitLine1, limitLine2) {
        const pan = newC.clone().sub(oldC);
        const newA = A.clone().add(pan);
        this.applyLimitAndProjectVector(sidePoint1, sidePoint2, newA, validPoint, 0, limitLine1);
        const newB = B.clone().add(pan);
        this.applyLimitAndProjectVector(sidePoint1, sidePoint2, newB, validPoint, 0, limitLine2);
        return A.distanceTo(newA) > B.distanceTo(newB) ? newC.copy(oldC.clone().add(newB.sub(B))) : newC.copy(oldC.clone().add(newA.sub(A)));
    }
    /**
     * Sets a fixed point to newPos on a vector if A or B go out of limit.
     */
    static applyLimitAndSetVectorCenterLinePoint(sidePoint1, sidePoint2, oldC, newC, A, B, validPoint, limitLine1, limitLine2) {
        !validPoint && (validPoint = oldC);
        const pan = newC.clone().sub(oldC);
        const newA = A.clone().add(pan);
        this.applyLimitAndSetVector(sidePoint1, sidePoint2, newA, A, validPoint, 0, limitLine1);
        const newB = B.clone().add(pan);
        this.applyLimitAndSetVector(sidePoint1, sidePoint2, newB, B, validPoint, 0, limitLine2);
        return A.distanceTo(newA) > B.distanceTo(newB) ? newC.copy(oldC.clone().add(newB.sub(B))) : newC.copy(oldC.clone().add(newA.sub(A)));
    }
    // TODO invesetigate this tolerance optional
    static applyMultiLimits(newPos, oldPos, rules, tolerance, limitLines = []) {
        const invalidLimits = this.getInvalidLimits(newPos, oldPos, rules, tolerance, limitLines);
        if (invalidLimits.length > 0) {
            const validLimitedPoint = this.getValidatedPoints(rules, invalidLimits);
            if (validLimitedPoint.length > 0) {
                return newPos.copy(VectorUtils.getNearestPoint(newPos, ...validLimitedPoint));
            }
            else {
                const validIntersections = this.getValidIntersections(rules);
                if (validIntersections.length > 0) {
                    return newPos.copy(VectorUtils.getNearestPoint(newPos, ...validIntersections));
                }
                else {
                    return newPos.copy(oldPos);
                }
            }
        }
        return newPos;
    }
    static getInvalidLimits(newPos, oldPos, sidePoints, tolerance, limitLines) {
        const invalidLimits = [];
        for (let i = 0; i < sidePoints.length; i++) {
            const limit = sidePoints[i];
            const validPoint = limit.v || oldPos;
            limit.t = limit.t ?? tolerance;
            const distDir = VectorUtils.getPerpendicularWithDirectionBasedOnPoint(limit.a, limit.b, validPoint, Consts.planeNormal).setLength(limit.t || this.tolerance);
            limit.a = limit.a.clone().add(distDir);
            limit.b = limit.b.clone().add(distDir);
            limit.validPosition = validPoint.clone().add(distDir);
            if (!VectorUtils.arePointsOnSameSide(limit.a, limit.b, Consts.planeNormal, limit.validPosition, newPos)) {
                limit.limitedPoint = VectorUtils.projectOnVector(newPos, limit.a, limit.b).add(distDir.setLength(this.tolerance));
                invalidLimits.push(limit);
            }
            limitLines[i]?.updateLimit(limit.a, limit.b);
        }
        return invalidLimits;
    }
    static getValidatedPoints(sidePoints, invalidLimit) {
        const validLimitedPoint = [];
        for (const limit of invalidLimit) {
            this.validatePoint(limit.limitedPoint, sidePoints, limit) && validLimitedPoint.push(limit.limitedPoint);
        }
        return validLimitedPoint;
    }
    static validatePoint(point, sidePoints, limitExcluded1, limitExcluded2) {
        for (const limit of sidePoints) {
            if (limit !== limitExcluded1 && limit !== limitExcluded2 && !VectorUtils.arePointsOnSameSide(limit.a, limit.b, Consts.planeNormal, limit.validPosition, point)) {
                return false;
            }
        }
        return true;
    }
    static getValidIntersections(sidePoints) {
        const validIntersections = [];
        for (let i = 0; i < sidePoints.length - 1; i++) {
            for (let j = i + 1; j < sidePoints.length; j++) {
                const intersection = VectorUtils.lines2DIntersection(sidePoints[i].a, sidePoints[i].b, sidePoints[j].a, sidePoints[j].b);
                intersection && this.validatePoint(intersection, sidePoints, sidePoints[i], sidePoints[j]) && validIntersections.push(intersection);
            }
        }
        return validIntersections;
    }
}
/**
 * Default tolerance used when apply limits.
 */
LimitUtils.tolerance = 10 ** -10; //LOOK OUT not affected by scale factor
//# sourceMappingURL=LimitUtils.js.map